import Phaser from 'phaser';

// Small scene-navigation history. The Back button on terminal scenes
// (Inbox / Settings / HowTo / About) and any other "secondary" scene
// pops from this stack so users return to where they came from —
// e.g. Game → Settings → Back returns to Game, not the home screen.
//
// AppNav and SectionTabs route transitions push the current scene
// onto the stack before starting the target scene. Back pops and
// falls back to Home when the stack is empty (or the caller specifies
// a different fallback).
//
// State is module-scoped (effectively a singleton) — Phaser's scene
// manager is itself a singleton per Phaser.Game instance, so a single
// shared history matches that lifecycle. Cleared on hard navigation
// events (sign-in, sign-out) via clearNavHistory() if needed.

const history: string[] = [];

/** Navigate to a target scene, pushing the current scene onto the
 *  history stack so Back can return here. Skips the push when the
 *  target is the same scene (no real transition). */
export function navigateTo(
  current: Phaser.Scene,
  target: string,
  data?: Record<string, unknown>
): void {
  const fromKey = current.scene.key;
  if (target === fromKey) return;
  history.push(fromKey);
  if (data === undefined) {
    current.scene.start(target);
  } else {
    current.scene.start(target, data);
  }
}

/** Pop the history stack and navigate there. When empty, falls back
 *  to Home (or the explicit `fallback` argument). */
export function navigateBack(
  current: Phaser.Scene,
  fallback: string = 'Home'
): void {
  const target = history.pop() ?? fallback;
  current.scene.start(target);
}

/** Drop the entire history. Use on deep transitions where prior
 *  context no longer makes sense — sign-in / sign-out / starting a
 *  fresh game from a deep link, etc. Currently unused; reserved for
 *  the auth flows in a later slice. */
export function clearNavHistory(): void {
  history.length = 0;
}
